/**
 * UpcomingObligationsBlockController — bloc « À venir » du dashboard payant
 * (EPIC-030 / 30.4 — §15.D).
 *
 * Charge les 10 prochaines obligations via GET /upcoming-obligations au
 * connect() (pas de polling) et rend la liste via le <template>
 * data-target="itemTemplate" — aucun HTML inline JS. Verbatim maquette
 * `tableau-de-bord.html` (sélecteurs `nm-timeline` / `nm-pill--*`, lien pied
 * vers `/echeances`).
 *
 * Mapping `domain` → libellé + classe pill via `DOMAIN_PILL_MAP`. Pour un
 * domaine non reconnu, le libellé brut est rendu avec `nm-pill` neutre.
 *
 * Pas de bouton « Marquer comme fait » : le bloc ne liste que des
 * obligations système, chacune rend un lien `obligationLink` vers sa page
 * spécialisée (défaut spec §15.D — cf. 99.62). Le bouton POST réservé aux
 * rappels personnels n'existe que sur la page dédiée `/echeances`.
 *
 * États visibles : `loading` (au connect), `list` (≥1 item), `empty`
 * (0 item OU erreur réseau hors 401). 401 reste géré globalement par
 * `api.js`.
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import { getDomainPill } from "/js/utils/obligation_domain_mapping.js";

export default class UpcomingObligationsBlockController extends BaseController {
    static targets = ["loading", "list", "empty", "itemTemplate"];

    connect() {
        this.load();
    }

    async load() {
        this.showLoading();
        try {
            const response = await api.upcomingObligations.list({ perPage: 10 });
            const items = Array.isArray(response?.data) ? response.data : [];
            if (items.length === 0) {
                this.showEmpty();

                return;
            }
            this.renderItems(items);
        } catch {
            // 401 déjà géré globalement par api.js (redirect /connexion.html).
            // Toute autre erreur → état vide neutre.
            this.showEmpty();
        }
    }

    renderItems(items) {
        this.listTarget.replaceChildren();
        for (const item of items) {
            const fragment = this.itemTemplateTarget.content.cloneNode(true);
            this.fillItem(fragment, item);
            this.listTarget.appendChild(fragment);
        }
        this.loadingTarget.hidden = true;
        this.emptyTarget.hidden = true;
        this.listTarget.hidden = false;
    }

    fillItem(fragment, item) {
        const whenEl = fragment.querySelector('[data-slot="dateRelative"]');
        const domainPillEl = fragment.querySelector('[data-slot="domainPill"]');
        const statusBadgeEl = fragment.querySelector('[data-slot="statusBadge"]');
        const linkEl = fragment.querySelector('[data-slot="obligationLink"]');
        const labelEl = fragment.querySelector('[data-slot="label"]');
        const markBtnEl = fragment.querySelector('[data-slot="markAsDoneBtn"]');

        const dueDate = item?.dueDate ?? "";
        if (whenEl) {
            whenEl.textContent = this.formatRelativeDate(dueDate);
            if (dueDate) {
                whenEl.setAttribute("title", dueDate);
            }
        }

        if (domainPillEl) {
            const mapping = getDomainPill(item?.domain);
            domainPillEl.className = mapping.class;
            const dot = domainPillEl.querySelector(".nm-pill__dot");
            domainPillEl.replaceChildren();
            if (dot) domainPillEl.appendChild(dot);
            else {
                const newDot = document.createElement("span");
                newDot.className = "nm-pill__dot";
                domainPillEl.appendChild(newDot);
            }
            domainPillEl.append(mapping.label);
        }

        if (statusBadgeEl) {
            this.fillStatusBadge(statusBadgeEl, item?.status);
        }

        if (linkEl) {
            if (item?.actionUrl) {
                linkEl.setAttribute("href", item.actionUrl);
            } else {
                linkEl.removeAttribute("href");
            }
        }

        if (labelEl) {
            labelEl.textContent = item?.label ?? "";
        }

        if (markBtnEl) {
            // Le bloc dashboard ne liste que des obligations système (pas de
            // fetch des rappels personnels) : aucun bouton POST « Marquer comme
            // fait ». La réalisation s'enregistre sur la page spécialisée via le
            // lien posé ci-dessus (défaut spec §15.D — cf. 99.62).
            markBtnEl.remove();
        }
    }

    fillStatusBadge(badgeEl, status) {
        if (status === "overdue") {
            badgeEl.className = "nm-pill nm-pill--red";
            badgeEl.hidden = false;
            badgeEl.replaceChildren();
            const dot = document.createElement("span");
            dot.className = "nm-pill__dot";
            badgeEl.appendChild(dot);
            badgeEl.append("en retard");
        } else if (status === "not_applicable") {
            badgeEl.className = "nm-pill";
            badgeEl.hidden = false;
            badgeEl.replaceChildren();
            const dot = document.createElement("span");
            dot.className = "nm-pill__dot";
            badgeEl.appendChild(dot);
            badgeEl.append("non applicable");
        } else {
            badgeEl.hidden = true;
        }
    }

    showLoading() {
        this.loadingTarget.hidden = false;
        this.listTarget.hidden = true;
        this.emptyTarget.hidden = true;
    }

    showEmpty() {
        this.loadingTarget.hidden = true;
        this.listTarget.hidden = true;
        this.emptyTarget.hidden = false;
    }
}
